import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
} from 'shared/config/model/urls';
import {AUTO_SELECT_ID} from 'shared/lib/constants';

import BatchCreatePortMonitorType from 'features/port/model/PortMonitor/BatchCreatePortMonitorType';
import CreatePortMonitorType from 'features/port/model/PortMonitor/CreatePortMonitorType';
import IPortMonitor, {IPortMonitorInput} from 'features/port/model/PortMonitor/IPortMonitor';
import {
    buildCommonBatchRequest,
    IBatchOperationRequest,
    IBuildBatchRequestParams,
    normalizeTags,
} from 'entities/monitor';
import {idsToIri, normalizeIds} from 'shared/api/iriHelper';

import {getPortMonitorAddress, isValidPort, isValidPortMonitorIp} from './portMonitorHelper';
import {buildPortBatchEditPayload, PortBatchEditFormValues} from './portBatchEditFields';
import {parsePortTargets} from './parsePortTargets';

const PORT_DEFAULT_CHECK_INTERVAL = 5;
export const PORT_DEFAULT_TIMEOUT = 15;
export const PORT_NAME_MAX_LENGTH = 255;

export type PortBatchOperationsFormValues = PortBatchEditFormValues & {host_port: string};

export interface IPortFormContext {
    freeTariff: boolean,
    emailConfirmed: boolean,
}

const getDefaultLocations = (freeTariff: boolean): string[] => (!freeTariff ? [AUTO_SELECT_ID] : []);

export function getPortMonitorFormDefaults(
    item: IPortMonitor | undefined,
    {freeTariff, emailConfirmed}: IPortFormContext,
): CreatePortMonitorType {
    const locations = normalizeIds(item?.locations);

    return {
        ip: item?.ip ?? '',
        port: item ? `${item.port}` : '',
        name: item?.name ?? '',
        desired_state: item?.desired_state ?? 'open',
        check_interval: item?.check_interval ?? PORT_DEFAULT_CHECK_INTERVAL,
        timeout: item?.timeout ?? PORT_DEFAULT_TIMEOUT,
        locations: locations.length > 0 ? locations : getDefaultLocations(freeTariff),
        threshold: freeTariff ? 1 : (item?.threshold ?? 1),
        notify_by_email: item ? item.notify_by_email : emailConfirmed,
        telegram_chats: normalizeIds(item?.telegram_chats),
        max_chats: normalizeIds(item?.max_chats),
        additional_emails: normalizeIds(item?.additional_emails),
        tags: item?.tags?.map(tag => tag.label) ?? [],
    };
}

export function getPortBatchCreateDefaults({freeTariff, emailConfirmed}: IPortFormContext): BatchCreatePortMonitorType {
    return {
        targets: '',
        desired_state: 'open',
        check_interval: PORT_DEFAULT_CHECK_INTERVAL,
        timeout: PORT_DEFAULT_TIMEOUT,
        locations: getDefaultLocations(freeTariff),
        threshold: 1,
        notify_by_email: emailConfirmed,
        telegram_chats: [],
        max_chats: [],
        additional_emails: [],
        tags: [],
    };
}

export function getPortBatchOperationsDefaults(
    {freeTariff, emailConfirmed}: IPortFormContext,
): PortBatchOperationsFormValues {
    return {
        host_port: '',
        desired_state: 'open',
        check_interval: PORT_DEFAULT_CHECK_INTERVAL,
        timeout: PORT_DEFAULT_TIMEOUT,
        locations: getDefaultLocations(freeTariff),
        threshold: 1,
        notify_by_email: emailConfirmed,
        telegram_chats: [],
        max_chats: [],
        additional_emails: [],
        tags: [],
    };
}

export function getPortMonitorNamePlaceholder(ip: string | undefined, port: string | undefined): string {
    return isValidPortMonitorIp(ip ?? '') && isValidPort(port ?? '')
        ? getPortMonitorAddress((ip as string).trim(), Number(port))
        : 'ip:port';
}

function buildPortMonitorSettingsPayload(values: PortBatchEditFormValues) {
    const selectedLocations = (values.locations ?? []).filter(id => id !== AUTO_SELECT_ID);

    return {
        desired_state: values.desired_state,
        check_interval: values.check_interval,
        timeout: values.timeout,
        locations: idsToIri(selectedLocations, 'locations'),
        threshold: selectedLocations.length > 0 ? values.threshold : 1,
        notify_by_email: values.notify_by_email,
        telegram_chats: idsToIri(values.telegram_chats, TELEGRAM_CHATS_RESOURCE_NAME),
        max_chats: idsToIri(values.max_chats, MAX_CHATS_RESOURCE_NAME),
        additional_emails: idsToIri(values.additional_emails, ADDITIONAL_EMAILS_RESOURCE_NAME),
        tags: values.tags ? normalizeTags(values.tags) : [],
    };
}

export function buildPortMonitorInput(values: CreatePortMonitorType): IPortMonitorInput {
    return {
        ip: values.ip.trim(),
        port: Number(values.port),
        name: values.name?.trim() ?? '',
        ...buildPortMonitorSettingsPayload(values),
    };
}

export function buildPortBatchCreateRequest(values: BatchCreatePortMonitorType) {
    return {
        targets: parsePortTargets(values.targets).join('\n'),
        ...buildPortMonitorSettingsPayload(values),
    };
}

export function buildPortBatchOperationRequest({
    actionValue,
    formData,
    addedFields,
    selectedIds,
}: IBuildBatchRequestParams<PortBatchOperationsFormValues>): IBatchOperationRequest | undefined {
    switch (actionValue) {
        case 'edit': {
            const payload = buildPortBatchEditPayload(addedFields, formData, selectedIds);

            return payload.data ? {method: 'PUT', data: payload.data} : undefined;
        }
        case 'remove-by-target':
            return {method: 'DELETE', data: {host_port: parsePortTargets(formData.host_port).join('\n')}};
        default:
            return buildCommonBatchRequest(actionValue, selectedIds, formData.tags);
    }
}
